import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  TableSortLabel,
  Skeleton,
  TablePagination,
} from '@mui/material';
import {
  ArrowBack,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Rule as RuleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Block as BlockIcon,
  Analytics as AnalyticsIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  CheckCircle,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { suppressionListsAPI, suppressionRulesAPI, bulkImportAPI } from '../../../services/leadGenAPI';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';

const SuppressionListDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Log filters state
  const [logFilters, setLogFilters] = useState({
    start_date: '',
    end_date: '',
    was_suppressed: '',
    campaign_id: '',
  });

  // Campaign management state
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignUpdateLoading, setCampaignUpdateLoading] = useState(false);

  // Enhanced logs table features
  const [logsPage, setLogsPage] = useState(0);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(25);
  const [logsSearchTerm, setLogsSearchTerm] = useState('');
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [logsSortBy, setLogsSortBy] = useState('check_timestamp');
  const [logsSortOrder, setLogsSortOrder] = useState('desc');

  // Enhanced rules table features
  const [rulesPage, setRulesPage] = useState(0);
  const [rulesRowsPerPage, setRulesRowsPerPage] = useState(10);
  const [rulesSearchTerm, setRulesSearchTerm] = useState('');
  const [filteredRules, setFilteredRules] = useState([]);

  useEffect(() => {
    if (id) {
      fetchListDetails();
      fetchSuppressionLogs();
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 2 && list?.account_id) { // Campaigns tab
      fetchAvailableCampaigns();
    }
  }, [activeTab, list?.account_id]);

  // Apply logs filtering and search
  useEffect(() => {
    applyLogsFilters();
  }, [logs, logsSearchTerm, logsSortBy, logsSortOrder]);

  // Apply rules filtering and search
  useEffect(() => {
    applyRulesFilters();
  }, [list?.suppression_rules, rulesSearchTerm]);

  const applyLogsFilters = () => {
    let filtered = [...logs];

    // Apply search
    if (logsSearchTerm) {
      filtered = filtered.filter(log =>
        log.lead_email?.toLowerCase().includes(logsSearchTerm.toLowerCase()) ||
        log.lead_id?.toString().includes(logsSearchTerm) ||
        log.campaign_id?.toString().includes(logsSearchTerm)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[logsSortBy];
      let bValue = b[logsSortBy];

      if (logsSortBy === 'check_timestamp') {
        aValue = new Date(a.check_timestamp || 0);
        bValue = new Date(b.check_timestamp || 0);
      } else if (logsSortBy === 'lead_id') {
        aValue = a.lead_id || 0;
        bValue = b.lead_id || 0;
      } else if (logsSortBy === 'campaign_id') {
        aValue = a.campaign_id || 0;
        bValue = b.campaign_id || 0;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (logsSortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredLogs(filtered);
    setLogsPage(0); // Reset to first page when filters change
  };

  const handleLogsSort = (column) => {
    if (logsSortBy === column) {
      setLogsSortOrder(logsSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setLogsSortBy(column);
      setLogsSortOrder('asc');
    }
  };

  const clearLogsSearch = () => {
    setLogsSearchTerm('');
  };

  const applyRulesFilters = () => {
    if (!list?.suppression_rules) {
      setFilteredRules([]);
      return;
    }

    let filtered = [...list.suppression_rules];

    // Apply search
    if (rulesSearchTerm) {
      filtered = filtered.filter(rule =>
        rule.field_name?.toLowerCase().includes(rulesSearchTerm.toLowerCase()) ||
        rule.operator?.toLowerCase().includes(rulesSearchTerm.toLowerCase()) ||
        rule.value?.toLowerCase().includes(rulesSearchTerm.toLowerCase()) ||
        rule.rule_type?.toLowerCase().includes(rulesSearchTerm.toLowerCase())
      );
    }

    setFilteredRules(filtered);
    setRulesPage(0); // Reset to first page when filters change
  };

  const clearRulesSearch = () => {
    setRulesSearchTerm('');
  };

  const fetchListDetails = async () => {
    try {
      const response = await suppressionListsAPI.getList(id);
      setList(response);
    } catch (error) {
      console.error('Error fetching list details:', error);
      showSnackbar(`Failed to fetch list details: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };



  const fetchSuppressionLogs = async (filters = {}) => {
    setLogsLoading(true);
    try {
      const params = {
        suppression_list_id: id,
        ...filters
      };
      
      const response = await suppressionListsAPI.getSuppressionLogsWithFilters(params);
      setLogs(response.results || response);
    } catch (error) {
      console.error('Error fetching suppression logs:', error);
      showSnackbar(`Failed to fetch suppression logs: ${error.message}`, 'error');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await suppressionListsAPI.updateList(id, { status: newStatus });
      setList(prev => ({ ...prev, status: newStatus }));
      showSnackbar(`List status updated to ${newStatus}`, 'success');
    } catch (error) {
      console.error('Error updating list status:', error);
      showSnackbar(`Failed to update list status: ${error.message}`, 'error');
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await suppressionListsAPI.deleteList(id);
      showSnackbar('Suppression list deleted successfully', 'success');
      navigate('/lead-gen/suppression');
    } catch (error) {
      console.error('Error deleting suppression list:', error);
      showSnackbar(`Failed to delete suppression list: ${error.message}`, 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleExportRules = async () => {
    try {
      const blob = await suppressionListsAPI.exportRules(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${list?.name || 'suppression-list'}-rules.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showSnackbar('Rules exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting rules:', error);
      showSnackbar(`Failed to export rules: ${error.message}`, 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleFilterChange = (field, value) => {
    setLogFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    const activeFilters = Object.fromEntries(
      Object.entries(logFilters).filter(([_, value]) => value !== '')
    );
    fetchSuppressionLogs(activeFilters);
  };

  const handleClearFilters = () => {
    setLogFilters({
      start_date: '',
      end_date: '',
      was_suppressed: '',
      campaign_id: '',
    });
    fetchSuppressionLogs();
  };

  const fetchAvailableCampaigns = async () => {
    if (!list?.account_id) return;
    
    setCampaignsLoading(true);
    try {
      const response = await bulkImportAPI.getAccountCampaigns(list.account_id);
      setAvailableCampaigns(response);
    } catch (error) {
      console.error('Error fetching available campaigns:', error);
      showSnackbar(`Failed to fetch available campaigns: ${error.message}`, 'error');
    } finally {
      setCampaignsLoading(false);
    }
  };

  const handleAddCampaign = async (campaignId) => {
    setCampaignUpdateLoading(true);
    try {
      await suppressionListsAPI.addCampaignAssociationToSuppressionList(id, campaignId);
      await fetchListDetails();
      showSnackbar('Campaign added successfully', 'success');
    } catch (error) {
      console.error('Error adding campaign:', error);
      showSnackbar(`Failed to add campaign: ${error.message}`, 'error');
    } finally {
      setCampaignUpdateLoading(false);
    }
  };

  const handleRemoveCampaign = async (campaignId) => {
    setCampaignUpdateLoading(true);
    try {
      await suppressionListsAPI.removeCampaignAssociationFromSuppressionList(id, campaignId);
      await fetchListDetails();
      showSnackbar('Campaign removed successfully', 'success');
    } catch (error) {
      console.error('Error removing campaign:', error);
      showSnackbar(`Failed to remove campaign: ${error.message}`, 'error');
    } finally {
      setCampaignUpdateLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'contact': return <PersonIcon />;
      case 'company': return <BusinessIcon />;
      case 'geographic': return <LocationIcon />;
      case 'demographic': return <PersonIcon />;
      default: return <BlockIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'contact': return 'primary';
      case 'company': return 'secondary';
      case 'geographic': return 'info';
      case 'demographic': return 'warning';
      default: return 'default';
    }
  };

  // Helper function to parse rule value (handles JSON strings)
  const parseRuleValue = (value) => {
    try {
      if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
        return JSON.parse(value);
      }
      return value;
    } catch (error) {
      return value;
    }
  };

  // Helper function to format rule value for display
  const formatRuleValue = (rule) => {
    const value = parseRuleValue(rule.value);
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!list) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Suppression list not found or you don't have permission to view it.
        </Alert>
      </Box>
    );
  }

  const renderOverviewTab = () => (
    <Grid container spacing={3}>
      {/* Basic Information */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  {getTypeIcon(list.suppression_type)}
                  <Typography variant="h5">{list.name}</Typography>
                  <Chip 
                    label={list.suppression_type} 
                    size="small" 
                    color={getTypeColor(list.suppression_type)}
                    variant="outlined"
                  />
                  <Chip 
                    label={list.list_type === 'whitelist' ? 'Whitelist' : 'Blacklist'} 
                    size="small" 
                    color={list.list_type === 'whitelist' ? 'success' : 'error'}
                    variant="outlined"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" color="textSecondary" paragraph>
                  {list.description || 'No description provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  List Type
                </Typography>
                <Chip 
                  label={list.list_type === 'whitelist' ? 'Whitelist (Only allow matching leads)' : 'Blacklist (Exclude matching leads)'} 
                  size="small"
                  color={list.list_type === 'whitelist' ? 'success' : 'error'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Scope
                </Typography>
                <Chip 
                  label={list.is_global ? 'Global' : 'Campaign Specific'} 
                  size="small"
                  color={list.is_global ? 'primary' : 'secondary'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={list.status || 'inactive'}
                    color={getStatusColor(list.status || 'inactive')}
                    size="small"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={(list.status || 'inactive') === 'active'}
                        onChange={(e) => handleStatusChange(e.target.checked ? 'active' : 'inactive')}
                      />
                    }
                    label=""
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {formatDate(list.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {formatDate(list.updated_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Last Used
                </Typography>
                <Typography variant="body2">
                  {formatDate(list.last_used_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Account ID
                </Typography>
                <Typography variant="body2">
                  {list.account_id || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Rules */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" color="text.primary">
                Suppression Rules ({filteredRules.length} of {list.total_rules_count || list.suppression_rules?.length || 0})
              </Typography>
              <Box display="flex" gap={1}>
                <TextField
                  size="small"
                  placeholder="Search rules..."
                  value={rulesSearchTerm}
                  onChange={(e) => setRulesSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: rulesSearchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={clearRulesSearch}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/lead-gen/suppression/${id}/edit`)}
                >
                  Edit Rules
                </Button>
              </Box>
            </Box>
            
            {filteredRules.length > 0 ? (
              <Box>
                <List>
                  {filteredRules
                    .slice(rulesPage * rulesRowsPerPage, rulesPage * rulesRowsPerPage + rulesRowsPerPage)
                    .map((rule, index) => (
                      <ListItem
                        key={rule.id || index}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          backgroundColor: 'background.paper',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'primary.main',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <ListItemIcon>
                          <RuleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body1" color="text.primary" fontWeight={500}>
                              {(() => {
                                const isValueOperator = rule.operator !== 'is_empty' && rule.operator !== 'is_not_empty';
                                return isValueOperator 
                                  ? `${rule.field_name} ${rule.operator} "${formatRuleValue(rule)}"`
                                  : `${rule.field_name} ${rule.operator}`;
                              })()}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              Rule {rulesPage * rulesRowsPerPage + index + 1} - {rule.rule_type} (Priority: {rule.priority})
                            </Typography>
                          }
                        />
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={rule.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={rule.is_active ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </Box>
                      </ListItem>
                    ))}
                </List>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredRules.length}
                  rowsPerPage={rulesRowsPerPage}
                  page={rulesPage}
                  onPageChange={(event, newPage) => setRulesPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRulesRowsPerPage(parseInt(event.target.value, 10));
                    setRulesPage(0);
                  }}
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                  }
                  labelRowsPerPage="Rules per page:"
                />
              </Box>
            ) : (
              <Box py={3}>
                <Typography variant="body2" color="textSecondary" gutterBottom align="center">
                  {rulesSearchTerm
                    ? 'No rules match your search criteria'
                    : 'No rules configured for this suppression list.'
                  }
                </Typography>
                {rulesSearchTerm && (
                  <Box display="flex" justifyContent="center" mt={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearRulesSearch}
                      startIcon={<ClearIcon />}
                    >
                      Clear Search
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Statistics */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Statistics
            </Typography>
            
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Rules
                </Typography>
                <Typography variant="h4" color="primary">
                  {(list.total_rules_count || 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Configured rules
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Suppressions
                </Typography>
                <Typography variant="h5" color="secondary">
                  {(list.total_suppressions_count || 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  All time
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Campaigns Using List
                </Typography>
                              <Typography variant="h5" color="info.main">
                {list.campaigns?.length || 0}
              </Typography>
                <Typography variant="caption" color="textSecondary">
                  Associated campaigns
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  List Status
                </Typography>
                <Typography variant="h5" color="success.main">
                  {list.status || 'inactive'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Current status
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Quick Actions
            </Typography>
            
            <Box display="flex" flexDirection="column" gap={1}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/lead-gen/suppression/${id}/edit`)}
                fullWidth
              >
                Edit List
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<AnalyticsIcon />}
                onClick={() => setActiveTab(1)}
                fullWidth
              >
                View Analytics
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportRules}
                fullWidth
              >
                Export Rules
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                fullWidth
              >
                Delete List
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderAnalyticsTab = () => (
    <Grid container spacing={3}>
      {/* Analytics Overview */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Analytics Overview
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {list?.total_suppressions_count || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Suppressions
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="secondary">
                    {list?.total_rules_count || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Rules
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {list?.campaigns?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Campaigns Using List
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main">
                    {list?.suppression_type || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Suppression Type
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Rule Distribution */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Rule Distribution
            </Typography>
            {list?.suppression_rules && list.suppression_rules.length > 0 ? (
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Rule Types:
                </Typography>
                {Object.entries(list.suppression_rules.reduce((acc, rule) => {
                  acc[rule.rule_type] = (acc[rule.rule_type] || 0) + 1;
                  return acc;
                }, {})).map(([type, count]) => (
                  <Box key={type} display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">{type}</Typography>
                    <Typography variant="body2" fontWeight="bold">{count}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Alert severity="info">
                No rules configured for analytics.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Campaign Usage */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Campaign Usage
            </Typography>
            {list?.campaigns && list.campaigns.length > 0 ? (
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Associated Campaigns:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {list.campaigns.map((campaign) => (
                    <Chip
                      key={campaign.id}
                      label={campaign.name}
                      size="small"
                      variant="outlined"
                      color={campaign.active ? 'success' : 'default'}
                    />
                  ))}
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                No campaigns associated with this list.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Suppression Trends */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Suppression Trends
            </Typography>
            <Alert severity="info">
              Chart visualization coming soon. This will show suppression trends over time.
            </Alert>
          </CardContent>
        </Card>
      </Grid>

      {/* Geographic Distribution */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Geographic Distribution
            </Typography>
            <Alert severity="info">
              Map visualization coming soon. This will show geographic distribution of suppressions.
            </Alert>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderCampaignsTab = () => (
    <Grid container spacing={3}>
      {/* Associated Campaigns */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Associated Campaigns ({list?.campaigns?.length || 0})
            </Typography>
            
            {campaignUpdateLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : list?.campaigns && list.campaigns.length > 0 ? (
              <List>
                {list.campaigns.map((campaign) => (
                  <ListItem
                    key={campaign.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: 'background.paper',
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveCampaign(campaign.id)}
                        color="error"
                        disabled={campaignUpdateLoading}
                      >
                        <RemoveIcon />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      <BusinessIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" color="text.primary" fontWeight={500}>
                          {campaign.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {campaign.description || 'No description'}
                          {campaign.active && (
                            <Chip
                              label="Active"
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info">
                No campaigns are currently associated with this suppression list.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Available Campaigns */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Available Campaigns
            </Typography>
            
            {campaignsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : availableCampaigns.length > 0 ? (
              <List>
                {availableCampaigns
                  .filter(campaign => !list?.campaigns?.some(associated => associated.id === campaign.id))
                  .map((campaign) => (
                    <ListItem
                      key={campaign.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        backgroundColor: 'background.paper',
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleAddCampaign(campaign.id)}
                          color="primary"
                          disabled={campaignUpdateLoading}
                        >
                          <AddIcon />
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        <BusinessIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" color="text.primary" fontWeight={500}>
                            {campaign.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {campaign.description || 'No description'}
                            {campaign.active && (
                              <Chip
                                label="Active"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
              </List>
            ) : (
              <Alert severity="info">
                No available campaigns found for this account.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderLogsTab = () => (
    <Grid container spacing={3}>
      {/* Filters Card */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary">
              Filter Logs
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={logFilters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={logFilters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Suppression Result</InputLabel>
                  <Select
                    value={logFilters.was_suppressed}
                    label="Suppression Result"
                    onChange={(e) => handleFilterChange('was_suppressed', e.target.value)}
                  >
                    <MenuItem value="">All Results</MenuItem>
                    <MenuItem value="true">Suppressed</MenuItem>
                    <MenuItem value="false">Not Suppressed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Campaign</InputLabel>
                  <Select
                    value={logFilters.campaign_id}
                    label="Campaign"
                    onChange={(e) => handleFilterChange('campaign_id', e.target.value)}
                  >
                    <MenuItem value="">All Campaigns</MenuItem>
                    {list?.campaigns?.map((campaign) => (
                      <MenuItem key={campaign.id} value={campaign.id.toString()}>
                        {campaign.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    disabled={logsLoading}
                    startIcon={logsLoading ? <CircularProgress size={16} /> : null}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    disabled={logsLoading}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Logs Table */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" color="text.primary">
                Suppression Logs ({filteredLogs.length} of {logs.length})
              </Typography>
              <Box display="flex" gap={1}>
                <TextField
                  size="small"
                  placeholder="Search logs..."
                  value={logsSearchTerm}
                  onChange={(e) => setLogsSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: logsSearchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={clearLogsSearch}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fetchSuppressionLogs()}
                  disabled={logsLoading}
                  startIcon={logsLoading ? <CircularProgress size={16} /> : null}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
            
            {logsLoading ? (
              // Loading skeleton rows
              <Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Check Time</TableCell>
                        <TableCell>Lead ID</TableCell>
                        <TableCell>Lead Email</TableCell>
                        <TableCell>Campaign ID</TableCell>
                        <TableCell>Suppression Result</TableCell>
                        <TableCell>Matched Rules</TableCell>
                        <TableCell>Lead Data</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell><Skeleton variant="text" width={120} /></TableCell>
                          <TableCell><Skeleton variant="text" width={80} /></TableCell>
                          <TableCell><Skeleton variant="text" width={200} /></TableCell>
                          <TableCell><Skeleton variant="text" width={100} /></TableCell>
                          <TableCell><Skeleton variant="rectangular" width={100} height={24} /></TableCell>
                          <TableCell><Skeleton variant="text" width={150} /></TableCell>
                          <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : filteredLogs.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={logsSortBy === 'check_timestamp'}
                          direction={logsSortBy === 'check_timestamp' ? logsSortOrder : 'asc'}
                          onClick={() => handleLogsSort('check_timestamp')}
                        >
                          Check Time
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={logsSortBy === 'lead_id'}
                          direction={logsSortBy === 'lead_id' ? logsSortOrder : 'asc'}
                          onClick={() => handleLogsSort('lead_id')}
                        >
                          Lead ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Lead Email</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={logsSortBy === 'campaign_id'}
                          direction={logsSortBy === 'campaign_id' ? logsSortOrder : 'asc'}
                          onClick={() => handleLogsSort('campaign_id')}
                        >
                          Campaign ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Suppression Result</TableCell>
                      <TableCell>Matched Rules</TableCell>
                      <TableCell>Lead Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLogs
                      .slice(logsPage * logsRowsPerPage, logsPage * logsRowsPerPage + logsRowsPerPage)
                      .map((log) => (
                        <TableRow key={log.id} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(log.check_timestamp)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {log.lead_id || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="primary">
                              {log.lead_email || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {log.campaign_id || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.was_suppressed ? 'Suppressed' : 'Not Suppressed'}
                              size="small"
                              color={log.was_suppressed ? 'error' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              {log.matched_rules && log.matched_rules.length > 0 ? (
                                log.matched_rules.map((rule, index) => (
                                  <Typography key={index} variant="caption" display="block">
                                    {(() => {
                                      const isValueOperator = rule.operator !== 'is_empty' && rule.operator !== 'is_not_empty';
                                      return isValueOperator 
                                        ? `• ${rule.field_name} ${rule.operator} "${rule.value}"`
                                        : `• ${rule.field_name} ${rule.operator}`;
                                    })()}
                                  </Typography>
                                ))
                              ) : (
                                <Typography variant="caption" color="textSecondary">
                                  No rules matched
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View lead data">
                              <IconButton size="small">
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={filteredLogs.length}
                  rowsPerPage={logsRowsPerPage}
                  page={logsPage}
                  onPageChange={(event, newPage) => setLogsPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setLogsRowsPerPage(parseInt(event.target.value, 10));
                    setLogsPage(0);
                  }}
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                  }
                  labelRowsPerPage="Rows per page:"
                />
              </TableContainer>
            ) : (
              <Box py={3}>
                <Typography variant="body2" color="textSecondary" gutterBottom align="center">
                  {logsSearchTerm
                    ? 'No logs match your search criteria'
                    : 'No suppression logs found for this list. Try adjusting your filters or check back later.'
                  }
                </Typography>
                {logsSearchTerm && (
                  <Box display="flex" justifyContent="center" mt={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearLogsSearch}
                      startIcon={<ClearIcon />}
                    >
                      Clear Search
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/lead-gen/suppression')}
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h4" component="h1" color="text.primary">
            Suppression List Details
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/lead-gen/suppression/${id}/edit`)}
          >
            Edit
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Overview" />
          <Tab label="Analytics" />
          <Tab label="Campaigns" />
          <Tab label="Logs" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && renderOverviewTab()}
      {activeTab === 1 && renderAnalyticsTab()}
      {activeTab === 2 && renderCampaignsTab()}
      {activeTab === 3 && renderLogsTab()}

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Suppression List"
        message={`Are you sure you want to delete the suppression list "${list?.name}"? This action cannot be undone. All rules and data associated with this list will be permanently removed.`}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuppressionListDetail; 